/**
 * The product's variation as the personalizer's FIRST step
 * (docs/product-page-plan-20261031.md §2): the colour, the size, "Sa zvukom" —
 * tiles with the variation's picture and price, before any question, because
 * the template, the add-ons, the price and the delivery promise all follow it.
 *
 * Like page-options.ts, nothing is kept here: a tile sets WooCommerce's own
 * select and fires its change, so `found_variation`, the variation id, the
 * gallery, our variation bridge and every variation-conditioned question
 * follow exactly as if the shopper had used the page's swatches (which this
 * flow hides).
 */

export interface WooVariation {
  attributes: Record<string, string>;
  display_price?: number;
  display_regular_price?: number;
  is_in_stock?: boolean;
  is_purchasable?: boolean;
  variation_is_active?: boolean;
  image?: { src?: string; thumb_src?: string; gallery_thumbnail_src?: string };
}

export interface VariantChoice {
  value: string;
  label: string;
  image?: string;
  /**
   * Lowest price of the variations with this value and the other picks (else
   * with this value at all), shown when choices differ in price.
   */
  price?: number;
  regular?: number;
  disabled: boolean;
  /**
   * Buyable, but not with what the other attributes have picked (black only
   * without sound): those picks' names, "Crna". Still a tile one can take — the
   * conflicting picks are then cleared.
   */
  blockedBy?: string[];
  /** The selects ("attribute_boja-majice") whose picks are cleared when it is taken. */
  clears?: string[];
}

export interface VariantAttribute {
  /** The select's name, "attribute_boja". */
  name: string;
  label: string;
  value: string;
  choices: VariantChoice[];
  /** Whether the tiles show prices (the choices do not all cost the same). */
  priced: boolean;
}

export interface SelectSource {
  name: string;
  id?: string;
  value: string;
  options: ArrayLike<{ value: string; text: string; disabled: boolean }>;
}

export const VARIANT_TEXTS = {
  title: 'Opcije proizvoda',
  pick: (label: string): string => `Izaberi opciju „${label}“.`,
  pickAny: 'Izaberi opciju proizvoda.',
  soldOut: 'Rasprodato',
  /** Under a tile that the other picks rule out. */
  notWith: (names: readonly string[]): string => `Nema uz ${names.map((n) => `„${n}“`).join(' i ')}`,
  combo: 'Ova kombinacija je rasprodata. Izaberi drugu opciju.',
  reset: 'Poništi izbor',
};

const capital = (text: string): string => (text ? text.charAt(0).toLocaleUpperCase('sr-RS') + text.slice(1) : text);

/** "attribute_pa_boja-kucista" → "Boja kucista" when the page names nothing. */
function fallbackLabel(name: string): string {
  return capital(name.replace(/^attribute_/, '').replace(/^pa_/, '').replace(/[-_]+/g, ' ').trim()) || name;
}

/**
 * The attributes the step asks: WooCommerce's selects with more than one
 * choice, each choice with its picture (the swatch's own, else the variation
 * image), its price and whether anything with it can be bought.
 */
export function readVariantAttributes(
  selects: readonly SelectSource[],
  variations: readonly WooVariation[] | null,
  labelFor: (select: SelectSource) => string | undefined,
  swatchImage: (name: string, value: string) => string | undefined = () => undefined,
  /** Attributes already shown: kept when WooCommerce's filtering leaves them one choice. */
  keep: (name: string) => boolean = () => false,
): VariantAttribute[] {
  const out: VariantAttribute[] = [];
  /** A variation's own value for an attribute fits a pick ('' = any). */
  const fits = (v: WooVariation, name: string, value: string): boolean => {
    const own = v.attributes?.[name];
    return own === value || own === '' || own === undefined;
  };
  for (const select of selects) {
    const others = selects.filter((o) => o !== select && o.value !== '');
    const choices: VariantChoice[] = [];
    for (const option of Array.from(select.options)) {
      if (option.value === '') continue;
      const matching = (variations ?? []).filter((v) => fits(v, select.name, option.value));
      const buyable = matching.filter((v) => v.is_in_stock !== false && v.is_purchasable !== false && v.variation_is_active !== false);
      const disabled = option.disabled || (variations !== null && variations.length > 0 && buyable.length === 0);
      // With what the other attributes have picked: its price is that variation's (40cm + Sa zvukom), not the cheapest size's.
      const withPicks = buyable.filter((v) => others.every((o) => fits(v, o.name, o.value)));
      const ruledOut = !disabled && buyable.length > 0 && withPicks.length === 0;
      // The picks that rule it out on their own; when only together, all of them.
      const alone = others.filter((o) => !buyable.some((v) => fits(v, o.name, o.value)));
      const blockers = ruledOut ? (alone.length ? alone : others) : [];
      const pool = withPicks.length ? withPicks : buyable;
      const prices = pool.map((v) => Number(v.display_price)).filter((p) => Number.isFinite(p));
      const price = prices.length ? Math.min(...prices) : undefined;
      const atPrice = pool.find((v) => Number(v.display_price) === price);
      const exact = matching.find((v) => v.attributes?.[select.name] === option.value);
      const image = swatchImage(select.name, option.value) ?? exact?.image?.thumb_src ?? exact?.image?.src;
      choices.push({
        value: option.value,
        label: option.text.trim() || option.value,
        ...(image ? { image } : {}),
        ...(price !== undefined ? { price } : {}),
        ...(atPrice && Number(atPrice.display_regular_price) > Number(price) ? { regular: Number(atPrice.display_regular_price) } : {}),
        disabled,
        ...(blockers.length ? { blockedBy: blockers.map((o) => optionText(o, o.value)), clears: blockers.map((o) => o.name) } : {}),
      });
    }
    if (choices.length === 0 || (choices.length < 2 && !keep(select.name))) continue;
    const priced = new Set(choices.map((c) => c.price).filter((p) => p !== undefined)).size > 1;
    out.push({
      name: select.name,
      label: capital((labelFor(select) ?? '').replace(/[:*]/g, '').trim()) || fallbackLabel(select.name),
      value: select.value,
      choices,
      priced,
    });
  }
  return out;
}

/**
 * Selects with nothing picked and one choice left: nothing to ask, and nothing
 * the shopper could pick either (this flow hides the page's selects) — yet
 * WooCommerce refuses the add without it. Picked for them.
 */
export function singleChoices(selects: readonly SelectSource[]): Array<{ name: string; value: string }> {
  const out: Array<{ name: string; value: string }> = [];
  for (const select of selects) {
    if (select.value) continue;
    const open = Array.from(select.options).filter((o) => o.value !== '' && !o.disabled);
    if (open.length === 1) out.push({ name: select.name, value: open[0]!.value });
  }
  return out;
}

/** The step's title: the one attribute's name, or "Opcije proizvoda". */
export function variantStepTitle(attributes: readonly VariantAttribute[]): string {
  return attributes.length === 1 ? attributes[0]!.label : VARIANT_TEXTS.title;
}

/** A select's option text for a value ("Sa zvukom"), the value itself when the option is gone. */
function optionText(select: SelectSource, value: string): string {
  const option = Array.from(select.options).find((o) => o.value === value);
  return option?.text.trim() || value;
}

/**
 * What is still to be picked, or that the picks together cannot be bought
 * ('' when every attribute has a value that goes with the others).
 */
export function variantMissing(attributes: readonly VariantAttribute[]): string {
  const open = attributes.filter((a) => !a.value);
  if (open.length === 1) return VARIANT_TEXTS.pick(open[0]!.label);
  if (open.length > 1) return VARIANT_TEXTS.pickAny;
  const stuck = attributes.some((a) => a.choices.find((c) => c.value === a.value)?.blockedBy?.length);
  return stuck ? VARIANT_TEXTS.combo : '';
}

// ── The page ───────────────────────────────────────────────────────────────

/** CSS.escape where the browser has it; a quoted-value escape where it does not (old WebViews). */
function cssEscape(value: string): string {
  const css = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  return typeof css?.escape === 'function' ? css.escape(value) : value.replace(/["\\]/g, '\\$&');
}

function variationForm(): HTMLFormElement | null {
  return document.querySelector<HTMLFormElement>('form.variations_form');
}

function pageSelects(): HTMLSelectElement[] {
  const form = variationForm();
  return form ? [...form.querySelectorAll<HTMLSelectElement>('select[name^="attribute_"]')] : [];
}

function pageVariations(): WooVariation[] | null {
  const raw = variationForm()?.getAttribute('data-product_variations');
  if (!raw || raw === 'false') return null;
  try {
    const list = JSON.parse(raw) as unknown;
    return Array.isArray(list) ? (list as WooVariation[]) : null;
  } catch {
    return null;
  }
}

/**
 * A label's words without what swatch plugins add after the name: the picked
 * value ("Zvuk po želji: Bez zvuka"), which goes stale as tiles are picked.
 */
function labelText(el: Element | null | undefined): string | undefined {
  if (!el) return undefined;
  const copy = el.cloneNode(true) as Element;
  for (const extra of Array.from(copy.querySelectorAll('.wcva_selected_attribute, .wcva_attribute_sep, .selected-value, .woo-selected-variation-item-name'))) extra.remove();
  return copy.textContent ?? undefined;
}

function pageLabel(select: SelectSource): string | undefined {
  const id = select.id;
  const byFor = id ? labelText(document.querySelector<HTMLLabelElement>(`label[for="${cssEscape(id)}"]`)) : undefined;
  if (byFor?.trim()) return byFor;
  // WooSwatches puts the name in its own span, a row above the select (the
  // cell around it also holds the picked value).
  const el = document.querySelector(`select[name="${cssEscape(select.name)}"]`);
  const table = el?.closest('table.variations, .variations');
  const row = el?.closest('tr');
  const above = row?.previousElementSibling;
  const title = labelText(row?.querySelector('.swatchtitlelabel'))
    ?? labelText(above?.querySelector('.swatchtitlelabel'))
    ?? labelText(above?.querySelector('th label, td.label'))
    ?? labelText(table?.querySelector('.swatchtitlelabel'));
  return title ?? undefined;
}

/** The swatch plugin's own picture for a value (its label's background), next to that select. */
function pageSwatchImage(name: string, value: string): string | undefined {
  const select = document.querySelector(`select[name="${cssEscape(name)}"]`);
  const scope: ParentNode = select?.closest('td, .value, tr') ?? document;
  const label = scope.querySelector<HTMLElement>(`[data-option="${cssEscape(value)}"]`);
  const bg = label?.style.backgroundImage ?? '';
  const match = /url\(["']?([^"')]+)["']?\)/.exec(bg);
  return match?.[1];
}

function pick(select: HTMLSelectElement, value: string): void {
  select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  const jq = (window as unknown as { jQuery?: (el: Element) => { trigger(name: string): void } }).jQuery;
  try {
    jq?.(select).trigger('change');
  } catch {
    /* the native event already went */
  }
}

export interface VariantPanel {
  element: HTMLElement;
  /** The page has a variation worth a step. */
  readonly active: boolean;
  title(): string;
  refresh(): void;
  /**
   * '' when everything is picked; else what to say — shown under the tiles
   * unless `quiet` (the popup says it in its footer, once).
   */
  validate(quiet?: boolean): string;
}

export interface VariantPanelOptions {
  format: (amount: number) => string;
  /** A choice was made (after WooCommerce has had its say). */
  onPick?: () => void;
}

export function createVariantPanel(options: VariantPanelOptions): VariantPanel {
  const root = document.createElement('div');
  root.className = 'pz-variant';
  const error = document.createElement('p');
  error.className = 'pz-variant-error';
  error.setAttribute('role', 'alert');
  error.hidden = true;

  // An attribute once shown stays: WooCommerce's filtering after a pick may
  // leave it one choice, and it must not vanish from under the shopper.
  const shown = new Set<string>();
  const read = (): VariantAttribute[] => {
    const list = readVariantAttributes(pageSelects(), pageVariations(), pageLabel, pageSwatchImage, (name) => shown.has(name));
    for (const a of list) shown.add(a.name);
    return list;
  };
  let attributes = read();

  /** Picks what has one choice left; true when anything was picked (WooCommerce re-filters). */
  const pickSingles = (): boolean => {
    let picked = false;
    for (const { name, value } of singleChoices(pageSelects())) {
      const select = pageSelects().find((s) => s.name === name);
      if (!select) continue;
      pick(select, value);
      picked = true;
    }
    return picked;
  };

  const draw = (): void => {
    attributes = read();
    root.textContent = '';
    for (const attribute of attributes) {
      const group = document.createElement('section');
      group.className = 'pz-variant-group';
      if (attributes.length > 1) {
        const heading = document.createElement('h4');
        heading.className = 'pz-variant-label';
        heading.textContent = attribute.label;
        group.append(heading);
      }
      const grid = document.createElement('div');
      grid.className = 'pz-variant-grid';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', attribute.label);
      for (const choice of attribute.choices) {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'pz-variant-tile';
        const on = choice.value === attribute.value;
        tile.classList.toggle('is-on', on);
        tile.classList.toggle('has-image', !!choice.image);
        tile.setAttribute('role', 'radio');
        tile.setAttribute('aria-checked', String(on));
        tile.disabled = choice.disabled;
        if (choice.image) {
          const img = document.createElement('img');
          img.className = 'pz-variant-img';
          img.src = choice.image;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          tile.append(img);
        }
        const name = document.createElement('span');
        name.className = 'pz-variant-name';
        name.textContent = choice.label;
        tile.append(name);
        tile.classList.toggle('is-blocked', !!choice.blockedBy);
        if (choice.disabled || choice.blockedBy) {
          const sold = document.createElement('span');
          sold.className = 'pz-variant-price';
          sold.textContent = choice.blockedBy ? VARIANT_TEXTS.notWith(choice.blockedBy) : VARIANT_TEXTS.soldOut;
          tile.append(sold);
        } else if (attribute.priced && choice.price !== undefined) {
          const price = document.createElement('span');
          price.className = 'pz-variant-price';
          if (choice.regular) {
            const was = document.createElement('del');
            was.textContent = options.format(choice.regular);
            price.append(was, document.createTextNode(' '));
          }
          price.append(document.createTextNode(options.format(choice.price)));
          tile.append(price);
        }
        const tick = document.createElement('span');
        tick.className = 'pz-variant-tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5 10 17 19 7"/></svg>';
        tile.append(tick);
        tile.addEventListener('click', () => {
          const select = pageSelects().find((s) => s.name === attribute.name);
          if (!select || choice.disabled) return;
          // Taking a tile the other picks rule out clears those picks: asked again, with what goes with it.
          for (const name of choice.clears ?? []) {
            const other = pageSelects().find((o) => o.name === name);
            if (other) pick(other, '');
          }
          pick(select, choice.value);
          error.hidden = true;
          // WooCommerce re-filters the other attributes after its own handler;
          // one left with a single choice is taken, as the shopper would have to.
          window.setTimeout(() => {
            if (pickSingles()) {
              window.setTimeout(() => {
                draw();
                options.onPick?.();
              }, 60);
              return;
            }
            draw();
            options.onPick?.();
          }, 60);
        });
        grid.append(tile);
      }
      group.append(grid);
      root.append(group);
    }
    // Two attributes can lock each other (red only in S, M only in blue): a way back to every choice.
    if (attributes.length > 1 && attributes.some((a) => a.value)) {
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'pz-variant-reset';
      reset.textContent = VARIANT_TEXTS.reset;
      reset.addEventListener('click', () => {
        const link = variationForm()?.querySelector<HTMLElement>('.reset_variations');
        if (link) link.click();
        else for (const select of pageSelects()) pick(select, '');
        window.setTimeout(() => {
          draw();
          options.onPick?.();
        }, 60);
      });
      root.append(reset);
    }
    root.append(error);
  };

  if (pickSingles()) window.setTimeout(draw, 60);
  draw();
  // A value set elsewhere (a reset, the cart's edit link, Woo's own filtering).
  variationForm()?.addEventListener('change', () => window.setTimeout(draw, 60));

  return {
    element: root,
    get active() {
      return attributes.length > 0;
    },
    title: () => variantStepTitle(attributes),
    refresh: draw,
    validate: (quiet = false) => {
      pickSingles();
      attributes = read();
      const message = variantMissing(attributes);
      error.textContent = message;
      error.hidden = quiet || !message;
      return message;
    },
  };
}
